import React, {useState, useRef, useEffect} from 'react';
import {Link, useNavigate} from 'react-router-dom';
import styled from 'styled-components';
import toast from 'react-hot-toast';
import {useAuth} from '../context/AuthContext';
import {useMerchant} from '../context/MerchantContext';
import {getFirstName, getInitials} from '../utils/userUtils';

const HeaderContainer = styled.header`
  background-color: ${props => props.theme.background || '#ffffff'};
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  padding: 1rem;
  position: fixed;
  top: 0;
  width: 100%;
  z-index: 1000;
  isolation: isolate;

  @media (max-width: 992px) {
    padding: 0.75rem 1rem 0.5rem;
  }
`;

const MobileCategoryTabs = styled.div<{ showRestaurantInfo: boolean }>`
  display: none;
  overflow-x: auto;
  white-space: nowrap;
  padding: 0.25rem 0.5rem;
  margin-top: 0;
  -webkit-overflow-scrolling: touch;
  &::-webkit-scrollbar {
    display: none;
  }

  @media (max-width: 992px) {
    display: ${props => props.showRestaurantInfo ? 'flex' : 'none'};
    gap: 0.5rem;
  }
`;

const MobileTab = styled.button<{ isActive: boolean; themeColor?: string }>`
  background: ${props => props.isActive ? (props.themeColor || '#e74c3c') : '#fff'};
  color: ${props => props.isActive ? 'white' : '#4a5568'};
  border: 1px solid ${props => props.isActive ? 'transparent' : '#eee'};
  padding: 0.35rem 0.85rem;
  border-radius: 50rem;
  font-size: 0.8rem;
  font-weight: 600;
  transition: all 0.2s;
  flex-shrink: 0;
  box-shadow: ${props => props.isActive ? '0 2px 4px rgba(0,0,0,0.1)' : '0 1px 2px rgba(0,0,0,0.05)'};

  &:active {
    transform: scale(0.95);
  }
`;

const HeaderContent = styled.div.attrs({ className: 'container' })`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;

  @media (max-width: 480px) {
    gap: 0.5rem;
  }
`;

interface CategoryIndicatorProps {
  visible: boolean;
}

const CategoryIndicator = styled.div<CategoryIndicatorProps & { themeColor?: string }>`
  display: flex;
  align-items: center;
  padding: 0.5rem 1rem;
  background-color: rgba(0, 0, 0, 0.05);
  border-radius: 4px;
  margin-left: 1rem;
  font-weight: 500;
  color: ${props => props.themeColor || '#e74c3c'};
  transition: opacity 0.3s ease;
  opacity: ${props => props.visible ? '1' : '0'};
  pointer-events: ${props => props.visible ? 'auto' : 'none'};
  cursor: pointer;
`;

const LogoContainer = styled.div`
  display: flex;
  align-items: center;
  flex: 1;

  @media (max-width: 480px) {
    flex: initial;
  }
`;

const Nav = styled.nav`
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 2;

  @media (max-width: 992px) {
    display: none;
  }
`;

const NavLink = styled(Link)<{ themeColor?: string }>`
  margin: 0 1rem;
  color: #333;
  text-decoration: none;
  font-weight: 500;

  &:hover {
    color: ${props => props.themeColor || '#e74c3c'};
  }

  @media (max-width: 768px) {
    margin: 0.5rem 0;
  }
`;

const AuthContainer = styled.div`
  flex: 1;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 0.5rem;
`;

const LoginButton = styled.button<{ themeColor?: string }>`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background-color: ${props => props.themeColor || '#e74c3c'};
  color: white;
  border: none;
  border-radius: 8px;
  padding: 0.6rem 1.2rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;

  &:hover {
    filter: brightness(0.9);
    transform: translateY(-1px);
  }

  /* Mantém o botão visível também no mobile, apenas mais compacto. */
  @media (max-width: 992px) {
    padding: 0.55rem 0.9rem;
    font-size: 0.9rem;
  }

  @media (max-width: 480px) {
    padding: 0.5rem 0.7rem;
  }
`;

// Em telas muito pequenas mostramos só o ícone para não estourar a largura.
const LoginButtonLabel = styled.span`
  @media (max-width: 480px) {
    display: none;
  }
`;

const ShareButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: transparent;
  color: #4a5568;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 0.6rem;
  font-size: 1.1rem;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background-color: #f7fafc;
    border-color: #cbd5e0;
  }
`;

const UserMenu = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

const UserButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  background-color: #f8fafc;
  color: #2d3748;
  border: 1px solid #e2e8f0;
  border-radius: 50px;
  padding: 0.4rem 0.6rem 0.4rem 0.4rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background-color: #f1f5f9;
    border-color: #cbd5e0;
  }

  @media (max-width: 480px) {
    padding: 0.3rem;
    gap: 0;
  }
`;

const Avatar = styled.div<{ themeColor?: string }>`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background-color: ${props => props.themeColor || '#e74c3c'};
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.85rem;
  font-weight: 700;
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const UserName = styled.span`
  font-size: 0.9rem;
  max-width: 100px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  @media (max-width: 480px) {
    display: none;
  }
`;

const UserDropdown = styled.div`
  position: absolute;
  right: 0;
  top: calc(100% + 10px);
  background-color: white;
  min-width: 200px;
  box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1);
  z-index: 1000;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  overflow: hidden;
  animation: slideIn 0.2s ease-out;
`;

const DropdownHeader = styled.div`
  padding: 1rem;
  background-color: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
`;

const DropdownUserEmail = styled.div`
  font-size: 0.8rem;
  color: #718096;
  word-break: break-all;
`;

const DropdownItem = styled.div<{ themeColor?: string }>`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  color: #4a5568;
  padding: 0.85rem 1rem;
  text-decoration: none;
  cursor: pointer;
  font-size: 0.9rem;
  font-weight: 500;
  transition: all 0.2s;

  &:hover {
    background-color: #f7fafc;
    color: ${props => props.themeColor || '#e74c3c'};
  }

  i {
    width: 16px;
    text-align: center;
    font-size: 1rem;
    color: #a0aec0;
  }

  &:hover i {
    color: ${props => props.themeColor || '#e74c3c'};
  }
`;

const Divider = styled.div`
  height: 1px;
  background-color: #e2e8f0;
  margin: 0.25rem 0;
`;

const LogoutItem = styled(DropdownItem)`
  color: #e53e3e;
  &:hover {
    background-color: #fff5f5;
    color: #c53030;
  }
  &:hover i {
    color: #c53030;
  }
`;

interface HeaderProps {
  currentCategory?: string;
  isCategoryVisible?: boolean;
  onCategoryClick?: () => void;
  logoUrl?: string;
  showRestaurantInfo?: boolean;
  categories?: any[];
  activeCategory?: string;
  onCategoryChange?: (categoryId: string) => void;
  searchQuery?: string;
  onSearchChange?: (query: string) => void;
}

const RestaurantHeaderInfo = styled.div<{ visible: boolean }>`
  display: flex;
  align-items: center;
  gap: 1rem;
  flex: 3;
  margin: 0 1rem;
  opacity: ${props => props.visible ? '1' : '0'};
  visibility: ${props => props.visible ? 'visible' : 'hidden'};
  transition: all 0.3s ease;

  @media (max-width: 992px) {
    display: none;
  }

  @media (max-width: 1200px) {
    gap: 0.5rem;
    margin: 0 0.5rem;
  }
`;

const SmallLogo = styled.img`
  width: 45px;
  height: 45px;
  border-radius: 10px;
  object-fit: cover;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  border: 1px solid #e2e8f0;
  cursor: pointer;

  @media (max-width: 480px) {
    width: 36px;
    height: 36px;
  }
`;

const RatingBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 0.25rem;
  background-color: #fff9db;
  color: #f08c00;
  padding: 0.2rem 0.5rem;
  border-radius: 6px;
  font-size: 0.85rem;
  font-weight: 700;
  border: 1px solid #ffe066;
  margin-right: 0.5rem;

  i {
    font-size: 0.75rem;
  }
`;

const HeaderCategorySelect = styled.div`
  position: relative;
  min-width: 200px;
`;

const DropdownButton = styled.button<{ themeColor?: string }>`
  width: 100%;
  padding: 0.6rem 1rem;
  padding-right: 2.5rem;
  border-radius: 10px;
  border: 1px solid #e2e8f0;
  background-color: #ffffff;
  font-size: 0.9rem;
  color: #2d3748;
  cursor: pointer;
  font-weight: 600;
  text-align: left;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  display: flex;
  align-items: center;
  justify-content: space-between;
  transition: all 0.2s ease;

  &:hover {
    border-color: #cbd5e0;
    box-shadow: 0 2px 4px rgba(0,0,0,0.05);
  }

  &:focus {
    outline: none;
    border-color: ${props => props.themeColor || '#e74c3c'};
    box-shadow: 0 0 0 3px ${props => props.themeColor ? `${props.themeColor}1a` : 'rgba(231, 76, 60, 0.1)'};
  }

  &::after {
    content: '';
    position: absolute;
    right: 1rem;
    top: 50%;
    transform: translateY(-50%);
    width: 1.1rem;
    height: 1.1rem;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%23a0aec0'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-position: center;
    background-size: contain;
  }
`;

const DropdownMenu = styled.div<{ isOpen: boolean }>`
  position: absolute;
  top: 110%;
  left: 0;
  width: 100%;
  background-color: white;
  border-radius: 10px;
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
  border: 1px solid #e2e8f0;
  z-index: 1000;
  max-height: 300px;
  overflow-y: auto;
  display: ${props => props.isOpen ? 'block' : 'none'};
  animation: slideIn 0.2s ease-out;

  @keyframes slideIn {
    from { opacity: 0; transform: translateY(-10px); }
    to { opacity: 1; transform: translateY(0); }
  }
`;

const CategoryDropdownItem = styled.div<{ isActive: boolean; themeColor?: string }>`
  padding: 0.75rem 1rem;
  font-size: 0.9rem;
  color: ${props => props.isActive ? (props.themeColor || '#e74c3c') : '#4a5568'};
  font-weight: ${props => props.isActive ? '700' : '500'};
  background-color: ${props => props.isActive ? (props.themeColor ? `${props.themeColor}1a` : '#fff5f5') : 'transparent'};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background-color: #f7fafc;
    color: ${props => props.themeColor || '#e74c3c'};
  }

  &:first-child {
    border-top-left-radius: 10px;
    border-top-right-radius: 10px;
  }

  &:last-child {
    border-bottom-left-radius: 10px;
    border-bottom-right-radius: 10px;
  }
`;

const HeaderSearchWrapper = styled.div`
  position: relative;
  flex: 1;
  max-width: 400px;
`;

const HeaderSearchInput = styled.input<{ themeColor?: string }>`
  width: 100%;
  padding: 0.6rem 2.5rem 0.6rem 2.5rem;
  border-radius: 10px;
  border: 1px solid #e2e8f0;
  background-color: #ffffff;
  font-size: 0.9rem;
  font-weight: 500;
  transition: all 0.2s ease;

  &::placeholder {
    color: #a0aec0;
  }

  &:hover {
    border-color: #cbd5e0;
  }

  &:focus {
    outline: none;
    border-color: ${props => props.themeColor || '#e74c3c'};
    box-shadow: 0 0 0 3px ${props => props.themeColor ? `${props.themeColor}1a` : 'rgba(231, 76, 60, 0.1)'};
  }
`;

const ClearSearchButton = styled.button`
  position: absolute;
  right: 0.85rem;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: #a0aec0;
  cursor: pointer;
  padding: 0.2rem;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.2s;
  z-index: 2;

  &:hover {
    color: #4a5568;
  }
`;

const HeaderSearchIcon = styled.i`
  position: absolute;
  left: 0.85rem;
  top: 50%;
  transform: translateY(-50%);
  color: #cbd5e0;
  font-size: 0.9rem;
`;

const MobileLogo = styled.img`
  display: none;
  width: 36px;
  height: 36px;
  border-radius: 8px;
  object-fit: cover;
  cursor: pointer;
  flex-shrink: 0;

  @media (max-width: 992px) {
    display: block;
  }
`;

const MobileSearchWrapper = styled.div<{ show: boolean; themeColor?: string }>`
  display: none;
  
  @media (max-width: 992px) {
    display: ${props => props.show ? 'flex' : 'none'};
    position: relative;
    flex: 1;
    margin: 0 0.5rem;
    animation: fadeIn 0.3s ease-out;
  }

  @keyframes fadeIn {
    from { opacity: 0; transform: translateY(-5px); }
    to { opacity: 1; transform: translateY(0); }
  }
`;

const MobileSearchInput = styled.input<{ themeColor?: string }>`
  width: 100%;
  padding: 0.45rem 2rem 0.45rem 2rem;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
  background-color: #f8fafc;
  font-size: 0.85rem;
  font-weight: 500;
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: ${props => props.themeColor || '#e74c3c'};
    background-color: #ffffff;
    box-shadow: 0 0 0 3px ${props => props.themeColor ? `${props.themeColor}1a` : 'rgba(231, 76, 60, 0.1)'};
  }
`;

const MobileSearchIcon = styled.i`
  position: absolute;
  left: 0.65rem;
  top: 50%;
  transform: translateY(-50%);
  color: #a0aec0;
  font-size: 0.8rem;
`;

const HomeIconMobile = styled.div<{ themeColor?: string }>`
  display: none;
  width: 36px;
  height: 36px;
  border-radius: 8px;
  background-color: #f8fafc;
  color: ${props => props.themeColor || '#e74c3c'};
  align-items: center;
  justify-content: center;
  cursor: pointer;
  border: 1px solid #e2e8f0;
  font-size: 1.2rem;

  @media (max-width: 992px) {
    display: flex;
  }

  @media (min-width: 993px) {
    display: none !important;
  }

  &:hover {
    background-color: #f1f5f9;
  }
`;

const Header: React.FC<HeaderProps> = ({ 
  currentCategory, 
  isCategoryVisible = false,
  onCategoryClick,
  logoUrl,
  showRestaurantInfo = false,
  categories = [],
  activeCategory = '',
  onCategoryChange,
  searchQuery = '',
  onSearchChange
}) => {
  const navigate = useNavigate();
  const { isAuthenticated, user, logout, setShowLoginModal } = useAuth();
  const { restaurant } = useMerchant();
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [isCategoryMenuOpen, setIsCategoryMenuOpen] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const userMenuRef = useRef<HTMLDivElement>(null);

  const themeColor = restaurant?.theme?.primary;

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setShowUserDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (scrollRef.current) {
      const activeTab = scrollRef.current.querySelector('[data-active="true"]');
      if (activeTab) {
        activeTab.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'center'
        });
      }
    }
  }, [activeCategory]);

  const selectedCategoryName = categories.find(c => c.id === activeCategory)?.name || 'Todas categorias';

  const handleShare = async () => {
    const shareData = {
      title: 'Leo Delivery - Cardápio Digital',
      text: 'Confira as delícias do Leo Delivery!',
      url: window.location.origin,
    };

    if (navigator.share) {
      try {
        await navigator.share(shareData);
      } catch (err) {
        console.error('Error sharing:', err);
      }
    } else {
      // Fallback: copy to clipboard
      try {
        await navigator.clipboard.writeText(window.location.origin);
        toast.success('Link do cardápio copiado!');
      } catch (err) {
        console.error('Could not copy text: ', err);
        toast.error('Erro ao copiar link.');
      }
    }
  };

  const handleLoginClick = () => {
    setShowLoginModal(true);
  };

  const handleOrdersClick = (e: React.MouseEvent) => {
    if (!isAuthenticated) {
      e.preventDefault();
      setShowLoginModal(true);
    }
  };

  const handleLogout = () => {
    logout();
    setShowUserDropdown(false);
    navigate('/');
  };

  const toggleUserDropdown = () => {
    setShowUserDropdown(!showUserDropdown);
  };

  return (
    <HeaderContainer>
      <HeaderContent>
        <LogoContainer>
          {showRestaurantInfo && logoUrl ? (
            <>
              <MobileLogo 
                src={logoUrl} 
                alt="Restaurant Logo" 
                onClick={() => navigate('/')}
              />
              <MobileSearchWrapper show={showRestaurantInfo} themeColor={themeColor}>
                <MobileSearchIcon className="fas fa-search" />
                <MobileSearchInput 
                  themeColor={themeColor}
                  type="text" 
                  placeholder="Buscar..." 
                  value={searchQuery}
                  onChange={(e) => onSearchChange?.(e.target.value)}
                />
                {searchQuery && (
                  <ClearSearchButton onClick={() => onSearchChange?.('')}>
                    <i className="fas fa-times" />
                  </ClearSearchButton>
                )}
              </MobileSearchWrapper>
            </>
          ) : (
            <HomeIconMobile 
              themeColor={themeColor}
              onClick={() => navigate('/')}
            >
              <i className="fas fa-home"></i>
            </HomeIconMobile>
          )}

          {showRestaurantInfo && logoUrl && (
            <div className="d-none d-lg-flex" style={{ alignItems: 'center', gap: '1rem' }}>
              <SmallLogo 
                src={logoUrl} 
                alt="Restaurant Logo" 
                onClick={() => navigate('/')}
              />
              <RestaurantHeaderInfo visible={showRestaurantInfo}>
                {restaurant?.rating !== undefined && restaurant?.rating > 0 && (
                  <RatingBadge>
                    <i className="fas fa-star"></i>
                    {restaurant.rating.toFixed(1)}
                  </RatingBadge>
                )}
                <HeaderCategorySelect>
                  <DropdownButton 
                    themeColor={themeColor}
                    onClick={(e) => {
                      e.stopPropagation();
                      setIsCategoryMenuOpen(!isCategoryMenuOpen);
                    }}
                  >
                    {selectedCategoryName}
                  </DropdownButton>
                  <DropdownMenu isOpen={isCategoryMenuOpen}>
                    <CategoryDropdownItem 
                      themeColor={themeColor}
                      isActive={activeCategory === ''} 
                      onClick={() => {
                        onCategoryChange?.('');
                        setIsCategoryMenuOpen(false);
                      }}
                    >
                      Todas categorias
                    </CategoryDropdownItem>
                    {categories.map((category) => (
                      <CategoryDropdownItem 
                        key={category.id} 
                        themeColor={themeColor}
                        isActive={activeCategory === category.id}
                        onClick={() => {
                          onCategoryChange?.(category.id);
                          setIsCategoryMenuOpen(false);
                        }}
                      >
                        {category.name}
                      </CategoryDropdownItem>
                    ))}
                  </DropdownMenu>
                </HeaderCategorySelect>

                <HeaderSearchWrapper>
                  <HeaderSearchIcon className="fas fa-search" />
                  <HeaderSearchInput 
                    themeColor={themeColor}
                    type="text" 
                    placeholder="Buscar no cardápio..." 
                    value={searchQuery}
                    onChange={(e) => onSearchChange?.(e.target.value)}
                  />
                  {searchQuery && (
                    <ClearSearchButton onClick={() => onSearchChange?.('')}>
                      <i className="fas fa-times" />
                    </ClearSearchButton>
                  )}
                </HeaderSearchWrapper>
              </RestaurantHeaderInfo>
            </div>
          )}
          
          {!showRestaurantInfo && currentCategory && (
            <div className="d-none d-md-flex align-items-center">
              <CategoryIndicator
                  themeColor={themeColor}
                  visible={isCategoryVisible}
                  onClick={onCategoryClick}
              >
                {currentCategory}
              </CategoryIndicator>
            </div>
          )}
        </LogoContainer>

        <Nav>
          <NavLink themeColor={themeColor} to="/">Início</NavLink>
          <NavLink themeColor={themeColor} to="/promocoes">Promoções</NavLink>
          <NavLink themeColor={themeColor} to="/pedidos" onClick={handleOrdersClick}>Pedidos</NavLink>
        </Nav>

        <AuthContainer>
          {/* Carrinho no mobile agora fica na BottomNav (aba Sacola). */}
          {isAuthenticated && user ? (
            <UserMenu ref={userMenuRef}>
              <UserButton onClick={toggleUserDropdown}>
                <Avatar themeColor={themeColor}>
                  {user.image ? (
                    <img src={user.image} alt={user.name} />
                  ) : (
                    getInitials(user.name)
                  )}
                </Avatar>
                <UserName>Olá, {getFirstName(user.name)}</UserName>
              </UserButton>
              {showUserDropdown && (
                <UserDropdown>
                  <DropdownHeader>
                    <div style={{ fontWeight: 600, fontSize: '0.9rem', color: '#2d3748' }}>{user.name}</div>
                    <DropdownUserEmail>{user.email}</DropdownUserEmail>
                  </DropdownHeader>
                  <DropdownItem themeColor={themeColor} onClick={() => { navigate('/pedidos'); setShowUserDropdown(false); }}>
                    <i className="fas fa-shopping-bag"></i>
                    Meus Pedidos
                  </DropdownItem>
                  <DropdownItem themeColor={themeColor} onClick={() => { navigate('/perfil'); setShowUserDropdown(false); }}>
                    <i className="fas fa-user"></i>
                    Meu Perfil
                  </DropdownItem>
                  <DropdownItem themeColor={themeColor} onClick={() => { navigate('/cashback'); setShowUserDropdown(false); }}>
                    <i className="fas fa-wallet"></i>
                    Minha Carteira
                  </DropdownItem>
                  <Divider />
                  <LogoutItem themeColor={themeColor} onClick={handleLogout}>
                    <i className="fas fa-sign-out-alt"></i>
                    Sair
                  </LogoutItem>
                </UserDropdown>
              )}
            </UserMenu>
          ) : (
            <LoginButton themeColor={themeColor} onClick={handleLoginClick}>
              <i className="fas fa-user"></i>
              <LoginButtonLabel>Login / Cadastro</LoginButtonLabel>
            </LoginButton>
          )}
        </AuthContainer>
      </HeaderContent>

      {/* Mobile Category Tabs */}
      {categories && categories.length > 0 && (
        <MobileCategoryTabs ref={scrollRef} showRestaurantInfo={showRestaurantInfo}>
          <MobileTab 
            isActive={activeCategory === ''} 
            data-active={activeCategory === ''}
            themeColor={themeColor}
            onClick={() => onCategoryChange?.('')}
          >
            Todos
          </MobileTab>
          {categories.map((category) => (
            <MobileTab 
              key={category.id} 
              isActive={activeCategory === category.id} 
              data-active={activeCategory === category.id}
              themeColor={themeColor}
              onClick={() => onCategoryChange?.(category.id)}
            >
              {category.name}
            </MobileTab>
          ))}
        </MobileCategoryTabs>
      )}
    </HeaderContainer>
  );
};

export default Header;
